import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../../redux/hooks';
import { editBrand, fetchAllBrands } from '../../../../redux/slices/BrandSlice';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../../shared-components/custom-input/CustomPhoneInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';

const BRAND_COUNTRY_OPTIONS = [
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'United States' },
  { value: 'SA', label: 'Saudi' },
  { value: 'UAE', label: 'United Arab Emirates' },
];

const BRAND_COUNTRY_PHONE_CODES: Record<string, string> = {
  IN: '91',
  US: '1',
  SA: '966',
  UAE: '971',
};

/**
 * Returns the default dialing code for a given brand country.
 * @param {string | undefined} country - The ISO country code.
 * @returns {string} The dialing code string.
 */
function getCountryCodeForBrandCountry(country?: string): string {
  if (!country) return '91';
  return BRAND_COUNTRY_PHONE_CODES[country] || '91';
}

/**
 * Resolves an ISO country value from a human-readable country label.
 * @param {string} countryLabel - The country label stored on the brand (e.g. "India")
 * @returns {string} The ISO country code (e.g. "IN"), or the original string if not found
 */
function resolveCountryValue(countryLabel: string): string {
  const found = BRAND_COUNTRY_OPTIONS.find(
    (opt) => opt.label.toLowerCase() === countryLabel?.toLowerCase(),
  );
  return found ? found.value : countryLabel || '';
}

interface EditBrandDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** The brand record to edit */
  brandData: any;
  /** Callback to close the modal */
  onClose: () => void;
  /** Called after the brand is successfully updated */
  onBrandUpdated: () => void;
}

/**
 * Slide-in drawer modal for editing an existing brand.
 * Pre-fills all fields from the current brand record and submits only changed fields.
 * @param {EditBrandDrawerProperties} param0 - Component props
 * @param {boolean} param0.open - The open prop
 * @param {any} param0.brandData - The brandData prop
 * @param {Function} param0.onClose - The onClose prop
 * @param {Function} param0.onBrandUpdated - The onBrandUpdated prop
 * @returns {React.JSX.Element} The rendered drawer
 */
function EditBrandDrawer({
  open,
  brandData,
  onClose,
  onBrandUpdated,
}: EditBrandDrawerProperties): React.JSX.Element {
  const { t } = useTranslation('deviceHub');
  const dispatch = useAppDispatch();

  const [companyName, setCompanyName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('91');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [companyError, setCompanyError] = useState('');
  const [brandError, setBrandError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  /** Populate fields whenever the drawer opens with new brand data */
  useEffect(() => {
    if (open && brandData) {
      setCompanyName(brandData.companyName || '');
      setBrandName(brandData.name || '');
      setCountry(resolveCountryValue(brandData.country || ''));
      setCountryCode(brandData.countryCode || getCountryCodeForBrandCountry(brandData.country));
      setEmail(brandData.email || '');
      setPhone(brandData.phoneNumber || '');
      setCompanyError('');
      setBrandError('');
      setCountryError('');
      setPhoneError('');
      setEmailError('');
      setShowFormErrors(false);
    }
  }, [open, brandData]);

  /**
   * Resets error state fields.
   */
  const resetErrors = () => {
    setCompanyError('');
    setBrandError('');
    setCountryError('');
    setPhoneError('');
    setEmailError('');
    setShowFormErrors(false);
  };

  /**
   * Handles closing the drawer and resetting error state.
   */
  const handleClose = () => {
    resetErrors();
    onClose();
  };

  /**
   * Handles phone number and country code field changes.
   * @param {string} name - Field name ('countryCode' or phone number field)
   * @param {string | number} value - The updated value
   */
  const handlePhoneChange = (name: string, value: string | number) => {
    if (name === 'countryCode') {
      setCountryCode(value.toString());
    } else {
      setPhone(value.toString());
    }
    if (phoneError) setPhoneError('');
  };

  /**
   * Handles country selection and syncs the dialing code.
   * @param {string} selectedCountry - ISO country code
   */
  const handleCountryChange = (selectedCountry: string) => {
    setCountry(selectedCountry);
    setCountryCode(getCountryCodeForBrandCountry(selectedCountry));
    if (countryError) setCountryError('');
  };

  /**
   * Validates and submits the brand update PATCH request.
   * Only sends fields that differ from the original brand data.
   */
  const handleSubmit = async () => {
    setShowFormErrors(true);
    resetErrors();

    if (!companyName.trim()) return;
    if (!brandName.trim()) return;
    if (!country) return;
    if (!email.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return;

    if (phone.trim() && (phone.trim().length < 9 || phone.trim().length > 15)) {
      setPhoneError('Phone number must be between 9 to 15 digits');
      return;
    }

    const resolvedCountryLabel =
      BRAND_COUNTRY_OPTIONS.find((opt) => opt.value === country)?.label || country;

    const payload: Record<string, any> = {
      companyName: companyName.trim(),
      name: brandName.trim(),
      country: resolvedCountryLabel,
      email: email.trim(),
    };

    if (phone.trim()) {
      payload.phoneNumber = phone.trim();
      payload.countryCode = countryCode.trim();
    } else {
      payload.phoneNumber = null;
      payload.countryCode = null;
    }

    setSubmitting(true);

    try {
      await dispatch(
        editBrand({ id: brandData._id || brandData.id, data: payload }),
      ).unwrap();

      setShowSuccess(true);
      dispatch(
        fetchAllBrands({
          page: 1,
          limit: 10,
          sortBy: 'desc',
          sortField: 'createdAt',
        }),
      );
      onBrandUpdated();
    } catch (error: any) {
      const message =
        error?.message || error?.response?.data?.message || t('somethingWentWrong');
      const lowerMessage = message.toLowerCase();

      if (lowerMessage.includes('phone') || lowerMessage.includes('mobile')) {
        setPhoneError(message);
      } else if (lowerMessage.includes('brand') || lowerMessage.includes('name already')) {
        setBrandError(message);
      } else if (lowerMessage.includes('company')) {
        setCompanyError(message);
      } else if (lowerMessage.includes('email') || lowerMessage.includes('mail')) {
        setEmailError(message);
      } else if (lowerMessage.includes('country')) {
        setCountryError(message);
      } else {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 500,
              message,
            },
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content drawer-wide">
            <div className="drawer-header">
              <h3 className="drawer-title">{t('editBrand')}</h3>
              <p className="drawer-subtitle">{t('editBrandDescription')}</p>
            </div>
            <div className="drawer-body">
              <div className="brand-fields-card">
                {/* Row 1 */}
                <div className="drawer-grid-2">
                  <CustomInput
                    type="text"
                    name="editCompanyName"
                    inputLabel={t('companyName')}
                    placeholder={t('enterCompanyName')}
                    value={companyName}
                    onChangeValue={(_, value) => {
                      setCompanyName(value.toString());
                      if (companyError) setCompanyError('');
                    }}
                    error={Boolean((showFormErrors && !companyName.trim()) || companyError)}
                    errorMessage={companyError || t('companyNameRequired')}
                    valueRequired
                    characterLimit={100}
                  />

                  <CustomInput
                    type="text"
                    name="editBrandName"
                    inputLabel={t('brandName')}
                    placeholder={t('enterBrandName')}
                    value={brandName}
                    onChangeValue={(_, value) => {
                      setBrandName(value.toString());
                      if (brandError) setBrandError('');
                    }}
                    error={Boolean((showFormErrors && !brandName.trim()) || brandError)}
                    errorMessage={brandError || t('brandNameRequired')}
                    valueRequired
                    characterLimit={100}
                  />
                </div>

                {/* Row 2 */}
                <SelectField
                  name="editBrandCountry"
                  inputLabel={t('country')}
                  options={BRAND_COUNTRY_OPTIONS}
                  placeholder={t('chooseCountry')}
                  selectedValue={country}
                  onChangeValue={(_, value) => handleCountryChange(value.toString())}
                  error={Boolean((showFormErrors && !country) || countryError)}
                  errorMessage={countryError || t('countryRequired')}
                  valueRequired
                  enableSearch
                />

                {/* Row 3 */}
                <CustomPhoneInput
                  name="editPhoneNumber"
                  inputLabel={t('phoneNumberOptional')}
                  placeholder={t('enterMobileNumber')}
                  type="number"
                  value={phone}
                  value1={countryCode}
                  onChangeValue={handlePhoneChange}
                  disableDropdown
                  error={Boolean(phoneError)}
                  errorMessage={phoneError}
                />

                {/* Row 4 */}
                <CustomInput
                  type="email"
                  name="editContactEmail"
                  inputLabel={t('email')}
                  placeholder={t('enterEmailId')}
                  value={email}
                  onChangeValue={(_, value) => {
                    setEmail(value.toString());
                    if (emailError) setEmailError('');
                  }}
                  error={Boolean(
                    (showFormErrors &&
                      (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) ||
                      emailError,
                  )}
                  errorMessage={
                    emailError || (!email.trim() ? t('emailRequired') : t('validEmailRequired'))
                  }
                  valueRequired
                />
              </div>
            </div>
            <div className="drawer-footer">
              <MyButton label={t('cancel')} buttonType="error" onClickFunc={handleClose} />
              <MyButton
                label={t('updateBrand')}
                buttonType="submit"
                onClickFunc={handleSubmit}
                disableButton={submitting}
                loading={submitting}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={t('brandUpdatedSuccessfully')}
            description=""
            onNotificationClose={() => {
              setShowSuccess(false);
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default EditBrandDrawer;
